import React, { useState } from 'react';

const initials = (employee) => {
  if (!employee) return null;
  const name = employee.display_name || employee.username || '';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDueDate = (isoDate) => {
  // isoDate is 'YYYY-MM-DD'; build a local date (not UTC) so "today" compares correctly.
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isOverdue = (isoDate) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

const TicketCard = ({ ticket, onOpen, onDragStart, isDoneColumn }) => {
  const customerReplied = ticket.last_reply_from === 'customer';
  const assignees = ticket.assignees || [];
  const activeWorkerIds = new Set((ticket.active_workers || []).map((e) => e.mavis_user_id));
  const overdue = !!ticket.due_date && !isDoneColumn && isOverdue(ticket.due_date);
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div
      className={`ticket-card ${ticket.raised_by_customer ? 'ticket-card-from-customer' : ''} ${customerReplied ? 'ticket-card-customer-replied' : ''} ${overdue ? 'ticket-card-overdue' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, ticket)}
      onClick={() => onOpen(ticket.id)}
      role="button"
      tabIndex={0}
      title={customerReplied ? 'Customer replied -- awaiting your response' : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(ticket.id);
        }
      }}
    >
      {ticket.tagged_for_me && (
        <span className="ticket-card-tag-badge" title="You were tagged on this ticket">You're tagged</span>
      )}
      {customerReplied && (
        <span className="ticket-card-reply-icon" title="Customer replied -- awaiting your response" aria-hidden="true">💬</span>
      )}
      <button
        type="button"
        className="ticket-card-preview-trigger"
        aria-label="Preview ticket"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setShowPreview(true)}
        onMouseLeave={() => setShowPreview(false)}
        onFocus={() => setShowPreview(true)}
        onBlur={() => setShowPreview(false)}
      >
        &#128065;
      </button>
      {showPreview && (
        <div className="ticket-card-preview-popover" onClick={(e) => e.stopPropagation()}>
          <div className="ticket-card-preview-title">{ticket.title}</div>
          {ticket.description_preview !== undefined ? (
            <p className="ticket-card-preview-desc">{ticket.description_preview || 'No description.'}</p>
          ) : null}
          <div className="ticket-card-preview-row"><strong>Key:</strong> {ticket.key}</div>
          <div className="ticket-card-preview-row"><strong>Priority:</strong> {ticket.priority}</div>
          {ticket.due_date && <div className="ticket-card-preview-row"><strong>Due:</strong> {formatDueDate(ticket.due_date)}</div>}
          <div className="ticket-card-preview-row">
            <strong>Assignees:</strong> {assignees.length ? assignees.map((a) => a.display_name || a.username).join(', ') : 'Unassigned'}
          </div>
          {ticket.customer_name && (
            <div className="ticket-card-preview-row"><strong>Customer:</strong> {ticket.customer_name}</div>
          )}
          {ticket.last_comment && (
            <div className="ticket-card-preview-last-comment">
              <div className="ticket-card-preview-row">
                <strong>Last comment</strong>
                {' '}({ticket.last_comment.author}
                {ticket.last_comment.visibility === 'customer' ? ', customer-visible' : ', internal'})
              </div>
              <p className="ticket-card-preview-desc">{ticket.last_comment.body_preview || '—'}</p>
            </div>
          )}
        </div>
      )}
      <div className="ticket-card-title">{ticket.title}</div>
      {ticket.customer_name && (
        <div className="ticket-card-customer" title="Customer">👤 {ticket.customer_name}</div>
      )}
      {ticket.label_list && ticket.label_list.length > 0 && (
        <div style={{ marginBottom: '0.4rem' }}>
          {ticket.label_list.map((label) => (
            <span key={label} className="label-chip">{label}</span>
          ))}
        </div>
      )}
      {ticket.due_date && (
        <div style={{ marginBottom: '0.4rem' }}>
          <span className={`due-date-chip ${overdue ? 'due-date-chip-overdue' : ''}`} title={overdue ? 'Overdue' : 'Due date'}>
            {overdue ? '⚠ ' : ''}Due {formatDueDate(ticket.due_date)}
          </span>
        </div>
      )}
      <div className="ticket-card-footer">
        <span>
          <span className="ticket-key">{ticket.key}</span>{' '}
          {ticket.raised_by_customer && <span className="customer-raised-chip" title="Raised by the customer">CUSTOMER</span>}{' '}
          <span className={`priority-badge priority-${ticket.priority}`}>
            {ticket.priority === 'highest' && <span aria-hidden="true" title="Highest priority">🔥 </span>}
            {ticket.priority}
          </span>
        </span>
        {assignees.length > 0 ? (
          <span style={{ display: 'flex', gap: '0.2rem' }}>
            {assignees.slice(0, 3).map((emp) => (
              <span
                key={emp.mavis_user_id} className="avatar"
                style={activeWorkerIds.has(emp.mavis_user_id) ? { boxShadow: '0 0 0 2px var(--danger-soft-border)' } : undefined}
                title={activeWorkerIds.has(emp.mavis_user_id) ? `${emp.display_name} -- working on it now` : emp.display_name}
              >
                {initials(emp)}
              </span>
            ))}
            {assignees.length > 3 && <span className="avatar" title={`+${assignees.length - 3} more`}>+{assignees.length - 3}</span>}
          </span>
        ) : (
          <span className="avatar" style={{ background: 'rgba(255,255,255,0.1)' }} title="Unassigned">?</span>
        )}
      </div>
    </div>
  );
};

export default TicketCard;
